/**
 * Customers Repository (Phase 5g).
 * Phase B/C mirror of Products: upsertCustomer + single-row put under NO_KV_BLOB.
 */

import {
  queueBusinessPayloadFieldWrite,
  queueEntityItemUpsert
} from '../storage/businessPayloadWrite';
import { publishDomainEvent, DOMAIN_EVENTS } from '../events/domainEventBus';
import {
  readCustomersFromBestSource,
  refreshCustomersReadCache
} from '../../utils/customersReadSource';
import { filterValidCustomers } from '../../utils/salesCacheSync';

function customerEntityId(customer) {
  if (!customer || typeof customer !== 'object') return '';
  if (customer.customerId != null && customer.customerId !== '') {
    return String(customer.customerId);
  }
  if (customer.id != null && customer.id !== '') return String(customer.id);
  return '';
}

async function mergeCustomerIntoCollection(businessId, customer, options = {}) {
  const id = customerEntityId(customer);
  if (!id) {
    throw new Error('customer id required');
  }

  const currentRaw = Array.isArray(options.currentCustomers)
    ? options.currentCustomers
    : await readCustomersFromBestSource(businessId);
  const current = filterValidCustomers(currentRaw);

  let matched = false;
  let mergedItem = customer;
  const next = current.map((c) => {
    const cid = customerEntityId(c);
    if (cid && cid === id) {
      matched = true;
      mergedItem = {
        ...c,
        ...customer,
        customerId: c.customerId ?? customer.customerId,
        id: c.id ?? customer.id
      };
      return mergedItem;
    }
    return c;
  });

  if (!matched) {
    if (options.allowInsert === false) {
      throw new Error('customer_not_found');
    }
    next.push(customer);
    mergedItem = customer;
  }

  return { next: filterValidCustomers(next), mergedItem, id };
}

async function shouldUseSingleRowCustomerPut() {
  try {
    const { isEntityStoreNoKvBlobEnabled } = await import('../storage/businessPayloadMeta');
    const { isEntityStoreEnabled } = await import('../storage/entityAggregateStore');
    return isEntityStoreEnabled() && isEntityStoreNoKvBlobEnabled();
  } catch {
    return false;
  }
}

export class KvCustomersRepository {
  /**
   * @param {string} businessId
   */
  async listCustomers(businessId) {
    const list = await readCustomersFromBestSource(businessId);
    return filterValidCustomers(list);
  }

  /**
   * @param {string} businessId
   * @param {Array} customers
   */
  async replaceCustomers(businessId, customers, options = {}) {
    const next = filterValidCustomers(Array.isArray(customers) ? customers : []);
    let prev = [];
    if (!options.fromCloudPull && !options.skipDeleteDiff) {
      try {
        prev = filterValidCustomers(await readCustomersFromBestSource(businessId));
      } catch {
        prev = [];
      }
    }
    await queueBusinessPayloadFieldWrite(businessId, 'customers', next);
    await refreshCustomersReadCache(businessId, next);
    publishDomainEvent(DOMAIN_EVENTS.CUSTOMERS_UPDATED, { businessId, customers: next });
    if (!options.fromCloudPull) {
      try {
        const { enqueueHybridEntitySync } = await import('../../services/hybridCatalogSync');
        const { enqueueHybridDeletesForRemoved } = await import('../../services/hybridDeleteSync');
        if (!options.skipDeleteDiff) {
          void enqueueHybridDeletesForRemoved(businessId, 'customer', prev, next);
        }
        const { listChangedHybridEntities } = await import('../../services/hybridDiff');
        const changed = listChangedHybridEntities('customer', prev, next);
        for (const item of changed) {
          void enqueueHybridEntitySync(businessId, 'customer', item);
        }
      } catch {
        /* optional */
      }
    }
    return next;
  }

  /**
   * @param {string} businessId
   * @param {object} customer
   * @param {{ currentCustomers?: Array, allowInsert?: boolean }} [options]
   */
  async upsertCustomer(businessId, customer, options = {}) {
    if (!businessId) {
      throw new Error('businessId required');
    }
    if (!customer || typeof customer !== 'object') {
      throw new Error('customer required');
    }

    const { next, mergedItem } = await mergeCustomerIntoCollection(
      businessId,
      customer,
      options
    );

    if (await shouldUseSingleRowCustomerPut()) {
      await queueEntityItemUpsert(businessId, 'customers', mergedItem, {
        nextCollection: next
      });
    } else {
      await queueBusinessPayloadFieldWrite(businessId, 'customers', next);
    }

    await refreshCustomersReadCache(businessId, next);
    publishDomainEvent(DOMAIN_EVENTS.CUSTOMERS_UPDATED, { businessId, customers: next });
    if (!options.fromCloudPull) {
      try {
        const { enqueueHybridEntitySync } = await import('../../services/hybridCatalogSync');
        const stamped = {
          ...mergedItem,
          updatedAt: mergedItem.updatedAt || new Date().toISOString()
        };
        void enqueueHybridEntitySync(businessId, 'customer', stamped);
      } catch {
        /* optional */
      }
    }
    return next;
  }
}
